/**
 * DOCUMENTATION:
 * Component: DesignPatternDashboard
 * Purpose: Central hub for the Software Design Patterns track.
 * * Note for Junior Developers:
 * - A Design Pattern is a "blueprint" for solving a recurring problem.
 * - Patterns are categorized into three groups:
 * 1. Creational: How objects are created.
 * 2. Structural: How objects are composed into larger structures.
 * 3. Behavioral: How objects communicate with each other.
 * - Don't over-engineer. Use a pattern only when it solves a specific complexity problem.
 */

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
} from "@mui/material";

/* Icons */
import SchemaIcon from '@mui/icons-material/Schema';
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArchitectureIcon from '@mui/icons-material/Architecture';

/* Project-specific components */
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";
import RadioOption from "../../../components/RadioOption";

const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function DesignPatternDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);

    const lessons = useMemo(() => [
        { title: "Intro: Why Patterns Matter", link: "/design-patterns-fundamentals" },
        { title: "Creational: Singleton & Factory", link: "/design-patterns-creational" },
        { title: "Creational: Builder & Prototype", link: "/design-patterns-builder" },
        { title: "Structural: Adapter & Decorator", link: "/design-patterns-structural" },
        { title: "Structural: Proxy & Facade", link: "/design-patterns-proxy" },
        { title: "Behavioral: Observer & Strategy", link: "/design-patterns-behavioral" },
        { title: "Behavioral: State & Command", link: "/design-patterns-state" },
        { title: "Patterns in React (HOCs & Compound)", link: "/design-patterns-react" },
        { title: "Design Pattern Practical Project", link: "/design-patterns-project" },
        { title: "Design Patterns Certification Quiz", link: "/design-patterns-quiz" },
    ], []);

    const filteredLessons = useMemo(() => {
        const safeQuery = (searchQuery || "").toLowerCase();
        return lessons.filter(lesson =>
            (lesson?.title || "").toLowerCase().includes(safeQuery)
        );
    }, [searchQuery, lessons]);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 4, mt: 4, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    SOFTWARE DESIGN PATTERNS
                </Typography>
            </Box>

            <LanguageTabs active="Design Patterns" />

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>
                {/* LEFT: Context */}
                <Grid item xs={12} md={5}>
                    <GlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}>
                            BLUEPRINT HUB
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", textAlign: "center" }}>
                            <ArchitectureIcon sx={{ fontSize: 50, color: "goldenrod", mb: 2 }} />
                            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                                Modern web development is built on top of patterns established decades ago.
                                Understanding these allows you to design components that are decoupled, testable,
                                and ready for enterprise-scale requirements.
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 3 }}>
                            <LectureTimer />
                        </Box>
                    </GlassCard>
                </Grid>

                {/* RIGHT: Curriculum */}
                <Grid item xs={12} md={7}>
                    <GlassCard>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a pattern..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: { color: "white", bgcolor: "rgba(255,255,255,0.05)", borderRadius: "8px" }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredLessons.map((lesson, i) => (
                                <TopicBox key={i}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <RadioOption />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {lesson.title}
                                        </Typography>
                                    </Box>
                                    <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                        <ViewButton size="small">Begin</ViewButton>
                                    </Link>
                                </TopicBox>
                            ))}
                        </Stack>
                    </GlassCard>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
